import { Badge, type BadgeProps } from '@sgme/ui';
import { FormattedMessage } from 'react-intl';

import type { BlotterEntryStatus } from '@/store/blotter/api/types/BlotterEntryStatus.ts';

type Props = {
  status: BlotterEntryStatus;
};

export const StatusBadge = ({ status }: Props) => {
  const translatedStatus = <FormattedMessage id={`blotter.status.${status}`} defaultMessage="Unknown" />;
  const [displayType, color] = statusDisplay[status] ?? ['text', 'secondary'];
  if (displayType === 'text') {
    return <span className={`fs-12 text-${color}`}>{translatedStatus}</span>;
  }
  return (
    <Badge isPill color={color as BadgeProps['color']} size="sm">
      {translatedStatus}
    </Badge>
  );
};

const statusDisplay: { [status in BlotterEntryStatus]: [display: 'text' | 'pill', color: string] } = {
  // success
  Done: ['text', 'success'],
  Finished: ['text', 'success'],
  'Partially done': ['text', 'success'],
  'Block Processed': ['pill', 'success'],
  BookingValidated: ['pill', 'success'],
  Cleared: ['pill', 'success'],
  'FO Validated': ['pill', 'success'],
  'RFQ Validated': ['pill', 'success'],
  Cancelling: ['pill', 'success'],
  Updating: ['pill', 'success'],
  'Liquidity Allocated': ['pill', 'success'],
  Netted: ['pill', 'success'],
  'Conversion Executed': ['pill', 'success'],

  // info
  New: ['pill', 'outline-info'],
  Watched: ['pill', 'outline-info'],
  'Not started': ['pill', 'outline-info'],
  Running: ['pill', 'outline-info'],
  Paused: ['pill', 'outline-info'],
  'Booking Pre Validated': ['pill', 'info'],
  'FO Pre-Validated': ['pill', 'info'],
  Executing: ['pill', 'info'],

  // warning
  BookingToBeChecked: ['pill', 'warning'],
  ClearingPending: ['pill', 'warning'],
  'Electronic CTP unmapped': ['pill', 'warning'],
  'Fxo MO Pending': ['pill', 'warning'],
  'Fxo Trader Pending': ['pill', 'warning'],
  'Hedge Pending': ['pill', 'warning'],
  'MO Validation Pending': ['pill', 'warning'],
  'Pending Full Termination': ['pill', 'warning'],
  'Pending Liquidity': ['pill', 'warning'],
  'Pending Migration': ['pill', 'warning'],
  'Pending RFQ': ['pill', 'warning'],
  'RFQ Pending': ['pill', 'warning'],
  'Sales Pending': ['pill', 'warning'],
  'Trader Pending': ['pill', 'warning'],
  'Waiting Sales': ['pill', 'warning'],
  Submitted: ['pill', 'warning'],

  // danger
  Cancelled: ['text', 'danger'],
  Rejected: ['text', 'danger'],
  InError: ['text', 'danger'],
  Destroyed: ['text', 'danger'],
  'FO Destroyed': ['text', 'danger'],
  'Sales Cancelled': ['text', 'danger'],
  'Trader Cancelled': ['text', 'danger'],

  // dark ?
  Initial: ['text', 'dark'],
  'Sales RFQ': ['text', 'dark'],
  'Trader RFQ': ['text', 'dark'],

  // secondary
  Expired: ['text', 'secondary'],
  'Missed Deal': ['text', 'secondary'],
  Simulation: ['text', 'secondary'],
  SimulDestroyed: ['text', 'secondary'],
  'RFQ Sales Cancelled': ['text', 'secondary'],
};
